import { ErrorBoundary } from '@sentry/react';
import { Button, Modal, ModalFooter, ModalHeader } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batchTrackMetricFilters } from '@/analytics/snowplow/tracking';
import { playerMetricGroupsAtom, selectedMetricsAtom } from '@/atoms/dataLocker/metrics';
import { isMetricFiltersModalOpenAtom } from '@/atoms/dataLocker/player/modal';
import { CheckboxPicker } from '@/components/checkboxPicker/CheckboxPicker';
import { GeneralErrorBoundaryFallback } from '@/components/errorBoundary/ErrorBoundaryFallback';
import { useGroupedOptions } from '@/hooks/useGroupedOptions';
import { usePrefetchPlayerMetricDistributions } from '@/hooks/usePrefetchPlayerMetricDistributions';
import { ColumnGroup } from '@/types/table';
import { toCheckboxGroups } from '@/utils/checkboxPicker';

type OnSelectionChange = ComponentProps<typeof CheckboxPicker>['onSelectionChange'];

const MetricFiltersModalBase = ({ metricGroups = [] }: { metricGroups?: ColumnGroup[] }) => {
  const { t } = useTranslation(['general', 'filters']);
  const setIsOpen = useSetAtom(isMetricFiltersModalOpenAtom);
  const [playerDataLockerSelectedMetrics, setPlayerDataLockerSelectedMetrics] = useAtom(selectedMetricsAtom);
  const [modalVisibleMetrics, setModalVisibleMetrics] = useState<string[]>(playerDataLockerSelectedMetrics);

  const sortedTranslatedMetricGroups = useGroupedOptions(metricGroups);
  const prefetch = usePrefetchPlayerMetricDistributions();

  const groups = toCheckboxGroups(
    sortedTranslatedMetricGroups,
    t('metricThreeSixtyIndicator', { ns: 'filters' }),
    t('metricPhysicalIndicator', { ns: 'filters' }),
  );

  const onSelectionChange: OnSelectionChange = (_event, nextMetrics) => {
    setModalVisibleMetrics(nextMetrics);
    prefetch(nextMetrics); // start fetching before Apply so sliders appear immediately on confirm
  };

  const onConfirm = async () => {
    // we only want to trigger a tracking event for newly selected metrics
    const newlySelectedMetrics = modalVisibleMetrics.filter(
      metric => !playerDataLockerSelectedMetrics.includes(metric),
    );
    await batchTrackMetricFilters(newlySelectedMetrics, false);
    setPlayerDataLockerSelectedMetrics(modalVisibleMetrics);
    setIsOpen(false);
  };

  return (
    <Modal
      ariaLabelledBy="metric-filters-modal"
      isOpen
      size="small"
      onCancel={() => setIsOpen(false)}
      onConfirm={onConfirm}
    >
      <ModalHeader id="metric-filters-modal">{t('addMetricFilter', { ns: 'filters' })}</ModalHeader>
      <div className="h-[420px]">
        <CheckboxPicker
          groups={groups}
          title={t('availableMetrics', { ns: 'filters' })}
          searchPlaceholder={t('metricSearchPlaceholder', { ns: 'filters' })}
          selectedValues={modalVisibleMetrics}
          onSelectionChange={onSelectionChange}
        />
      </div>
      <ModalFooter>
        <Button onClick={() => setIsOpen(false)} variant="ghost">
          {t('cancel')}
        </Button>
        <Button onClick={onConfirm} variant="primary">
          {t('apply')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const MetricFiltersModalWithData = () => {
  const metricDistributionGroups = useAtomValue(playerMetricGroupsAtom);

  return <MetricFiltersModalBase metricGroups={metricDistributionGroups} />;
};

export const MetricFiltersModal = () => {
  const isMetricFiltersModalOpen = useAtomValue(isMetricFiltersModalOpenAtom);

  return (
    isMetricFiltersModalOpen && (
      <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
        <Suspense fallback={<MetricFiltersModalBase />}>
          <MetricFiltersModalWithData />
        </Suspense>
      </ErrorBoundary>
    )
  );
};
